import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'
import ConfirmModal from '../ConfirmModal'

const EMPTY_FORM = {
  deal_id: '',
  amount: '',
  currency: 'GBP',
  notes: ''
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

function parseMoneyInput(value) {
  const parsed = Number(String(value || '').replace(/,/g, '').trim())
  return Number.isFinite(parsed) ? parsed : 0
}


const PROJECT_STATUS = {
  draft: { label: 'Draft', color: '#64748B', background: '#F8FAFC' },
  active: { label: 'Active', color: '#16A34A', background: '#DCFCE7' },
  completed: { label: 'Completed', color: '#4F46E5', background: '#EEF2FF' },
  archived: { label: 'Archived', color: '#475569', background: '#E2E8F0' }
}

function getProjectStatusMeta(status) {
  return PROJECT_STATUS[status] || PROJECT_STATUS.active
}

function getDealLabel(deal) {
  if (!deal) return ''
  return `${deal.clients?.name ? `${deal.clients.name} — ` : ''}${deal.name}`
}

export default function ProjectDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { orgId, can } = useUser()
  const canViewFinancials = can('financials.view')
  const canManageFinancials = can('financials.manage')
  const dealDropdownRef = useRef(null)

  const [project, setProject] = useState(null)
  const [allocations, setAllocations] = useState([])
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [dealSearch, setDealSearch] = useState('')
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false)
  const [clientFilter, setClientFilter] = useState('all')
  const [sortBy, setSortBy] = useState('client-asc')

  useEffect(() => {
    if (!orgId) return
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, orgId])

  useEffect(() => {
    function handleClickOutside(event) {
      if (dealDropdownRef.current && !dealDropdownRef.current.contains(event.target)) {
        setDealDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  async function fetchAll() {
    setLoading(true)
    setError(null)

    const [
      { data: projectData, error: projectError },
      { data: allocationData, error: allocationError },
      { data: dealsData, error: dealsError }
    ] = await Promise.all([
      supabase
        .from('secure_projects')
        .select('*')
        .eq('id', id)
        .single(),

      supabase
        .from('secure_deal_project_allocations')
        .select('*')
        .eq('project_id', id)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false }),

      supabase
        .from('secure_deals')
        .select('*')
        .eq('org_id', orgId)
        .order('name')
    ])

    if (projectError) {
      console.error('Failed to load project:', projectError)
      setError(`Failed to load project: ${projectError.message}`)
      setLoading(false)
      return
    }

    if (allocationError) {
      console.error('Failed to load project allocations:', allocationError)
      setError(`Failed to load project allocations: ${allocationError.message}`)
      setLoading(false)
      return
    }

    if (dealsError) {
      console.error('Failed to load deals:', dealsError)
      setError(`Failed to load deals: ${dealsError.message}`)
      setLoading(false)
      return
    }

    setProject(projectData)
    setAllocations(allocationData || [])
    setDeals(dealsData || [])
    setLoading(false)
  }

  function resetForm() {
    setForm(EMPTY_FORM)
    setDealSearch('')
    setDealDropdownOpen(false)
    setShowForm(false)
  }

  function selectDeal(deal) {
    setForm({
      ...form,
      deal_id: deal.id,
      currency: deal.currency || form.currency || 'GBP'
    })
    setDealSearch(getDealLabel(deal))
    setDealDropdownOpen(false)
  }

  async function addAllocation() {
    if (!form.deal_id) {
      toast.error('Please select a deal')
      return
    }

    const amountValue = parseMoneyInput(form.amount)

    if (!amountValue || amountValue <= 0) {
      toast.error('Please enter an amount')
      return
    }

    setSaving(true)

    const { data: createdAllocation, error: insertError } = await supabase
      .from('deal_project_allocations')
      .insert([
        withOrg({
          project_id: id,
          deal_id: form.deal_id,
          amount: amountValue,
          currency: form.currency,
          notes: form.notes || null
        }, orgId)
      ])
      .select('id')
      .single()

    setSaving(false)

    if (insertError) {
      console.error('Failed to allocate deal:', insertError)
      toast.error(`Failed to allocate deal to project: ${insertError.message}`)
      return
    }

    if (!createdAllocation?.id) {
      toast.error('The allocation was not created. Please try again.')
      return
    }

    toast.success('Deal allocated to project')
    resetForm()
    fetchAll()
  }

  async function deleteAllocation(allocationId) {
    const { error: deleteError } = await supabase
      .from('deal_project_allocations')
      .delete()
      .eq('id', allocationId)

    if (deleteError) {
      console.error('Failed to remove allocation:', deleteError)
      toast.error(`Failed to remove allocation: ${deleteError.message}`)
      return
    }

    toast.success('Allocation removed')
    setConfirmDelete(null)
    fetchAll()
  }

  async function updateProjectStatus(status) {
    const { error: updateError } = await supabase
      .from('projects')
      .update({ status })
      .eq('id', id)
      .eq('org_id', orgId)

    if (updateError) {
      console.error('Failed to update project status:', updateError)
      toast.error(`Failed to update project: ${updateError.message}`)
      return
    }

    toast.success(
      status === 'completed'
        ? 'Project marked complete'
        : status === 'archived'
          ? 'Project archived'
          : status === 'active'
            ? 'Project restored'
            : 'Project updated'
    )

    fetchAll()
  }

  const totalRevenue = allocations.reduce((sum, allocation) => {
    return sum + (Number(allocation.amount) || 0)
  }, 0)

  const allocationToDelete = confirmDelete
    ? allocations.find(a => a.id === confirmDelete)
    : null

  const filteredDeals = deals.filter(deal =>
    getDealLabel(deal).toLowerCase().includes(dealSearch.toLowerCase())
  )

  const clientOptions = [
    ...new Set(
      allocations
        .map(a => a.deals?.clients?.name)
        .filter(Boolean)
    )
  ].sort()

  const filteredAllocations = [...allocations]
    .filter(a => {
      if (clientFilter === 'all') return true
      return a.deals?.clients?.name === clientFilter
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'client-asc':
          return (a.deals?.clients?.name || '').localeCompare(b.deals?.clients?.name || '')
        case 'client-desc':
          return (b.deals?.clients?.name || '').localeCompare(a.deals?.clients?.name || '')
        case 'amount-desc':
          return Number(b.amount || 0) - Number(a.amount || 0)
        case 'amount-asc':
          return Number(a.amount || 0) - Number(b.amount || 0)
        case 'deal-asc':
          return (a.deals?.name || '').localeCompare(b.deals?.name || '')
        case 'deal-desc':
          return (b.deals?.name || '').localeCompare(a.deals?.name || '')
        default:
          return 0
      }
    })

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading...
      </div>
    )
  }

  if (error) {
    return (
      <div className="hv-page">
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/contracts')} style={{ marginBottom: '20px' }}>
          ← Back to Projects
        </button>
        <div style={{ background: 'var(--hv-red-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--hv-radius)', padding: '14px 18px', color: 'var(--hv-red-text)', fontSize: '13.5px' }}>
          {error}
        </div>
      </div>
    )
  }

  const statusMeta = getProjectStatusMeta(project?.status)

  return (
    <div className="hv-page">
      <div style={{ marginBottom: '20px' }}>
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/contracts')}>
          ← Back to Projects
        </button>
      </div>

      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">{project?.name}</h1>
          <p className="hv-page-subtitle">
            {formatDate(project?.start_date)} → {formatDate(project?.end_date)}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canManageFinancials && <button className="hv-btn hv-btn-primary" onClick={() => showForm ? resetForm() : setShowForm(true)}>
            {showForm ? 'Cancel' : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Allocate Deal
              </>
            )}
          </button>}
        </div>
      </div>

      <div
        className="hv-card"
        style={{
          padding: '16px 18px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginBottom: 6 }}>
            Project Status
          </div>

          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 800,
              color: statusMeta.color,
              background: statusMeta.background
            }}
          >
            {statusMeta.label}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {project?.status !== 'completed' && (
            <button className="hv-btn hv-btn-secondary" onClick={() => updateProjectStatus('completed')}>
              Mark Complete
            </button>
          )}

          {project?.status !== 'archived' && (
            <button className="hv-btn hv-btn-secondary" onClick={() => updateProjectStatus('archived')}>
              Archive
            </button>
          )}

          {project?.status !== 'active' && (
            <button className="hv-btn hv-btn-primary" onClick={() => updateProjectStatus('active')}>
              Restore Active
            </button>
          )}

          {project?.status !== 'draft' && (
            <button className="hv-btn hv-btn-ghost" onClick={() => updateProjectStatus('draft')}>
              Move to Draft
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div className="hv-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Project Notes</div>
          <div style={{ fontSize: '14px', color: 'var(--hv-text-primary)' }}>
            {project?.notes || 'No notes yet.'}
          </div>
        </div>

        {canViewFinancials && <div className="hv-card" style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Total Revenue</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>
            £{totalRevenue.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '4px' }}>
            {allocations.length} {allocations.length === 1 ? 'deal' : 'deals'} allocated
          </div>
        </div>}
      </div>

      {showForm && canManageFinancials && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '20px' }}>
            Allocate a deal to this project
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div ref={dealDropdownRef} style={{ position: 'relative' }}>
              <label className="hv-form-label">Deal</label>

              <input
                className="hv-input"
                type="text"
                placeholder="Search and select a deal..."
                value={dealSearch}
                onFocus={() => setDealDropdownOpen(true)}
                onChange={e => {
                  setDealSearch(e.target.value)
                  setForm({ ...form, deal_id: '' })
                  setDealDropdownOpen(true)
                }}
              />

              {dealDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '64px',
                  left: 0,
                  right: 0,
                  background: '#fff',
                  border: '1px solid var(--hv-border)',
                  borderRadius: '10px',
                  boxShadow: '0 12px 32px rgba(15,21,35,0.16)',
                  zIndex: 50,
                  maxHeight: '260px',
                  overflowY: 'auto',
                  padding: '6px'
                }}>
                  {filteredDeals.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--hv-text-muted)' }}>
                      No deals found
                    </div>
                  ) : (
                    filteredDeals.map(deal => (
                      <button
                        key={deal.id}
                        type="button"
                        onClick={() => selectDeal(deal)}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: form.deal_id === deal.id ? 'var(--hv-accent-light)' : 'transparent',
                          color: 'var(--hv-text-primary)',
                          padding: '9px 10px',
                          borderRadius: '8px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontFamily: 'var(--hv-font)'
                        }}
                      >
                        <div style={{ fontWeight: '500' }}>{deal.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '2px' }}>
                          {deal.clients?.name || 'No client'}
                          {deal.value ? ` · ${currencySymbol(deal.currency)}${Number(deal.value).toLocaleString()}` : ''}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="hv-form-label">Amount</label>
              <input
                className="hv-input"
                type="number"
                placeholder="0"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Currency</label>
              <select
                className="hv-select"
                value={form.currency}
                onChange={e => setForm({ ...form, currency: e.target.value })}
              >
                {['GBP', 'USD', 'EUR', 'AUD', 'CAD'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Notes / Rights Summary</label>
              <input
                className="hv-input"
                placeholder="e.g. Main stage branding, newsletter sponsorship, VIP lounge"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" onClick={addAllocation} disabled={saving}>
            {saving ? 'Saving...' : 'Save Allocation'}
          </button>
        </div>
      )}

      <div className="hv-card" style={{ padding: '18px', marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '18px'
          }}
        >
          <div>
            <label className="hv-form-label" style={{ marginBottom: '4px', display: 'block' }}>
              Client
            </label>

            <select
              className="hv-select"
              value={clientFilter}
              onChange={e => setClientFilter(e.target.value)}
            >
              <option value="all">All Clients</option>
              {clientOptions.map(client => (
                <option key={client} value={client}>
                  {client}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="hv-form-label" style={{ marginBottom: '4px', display: 'block' }}>
              Sort By
            </label>

            <select
              className="hv-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="client-asc">Client A → Z</option>
              <option value="client-desc">Client Z → A</option>
              <option value="amount-desc">Amount High → Low</option>
              <option value="amount-asc">Amount Low → High</option>
              <option value="deal-asc">Deal A → Z</option>
              <option value="deal-desc">Deal Z → A</option>
            </select>
          </div>

          <div
            style={{
              marginLeft: 'auto',
              color: 'var(--hv-text-muted)',
              fontSize: '13px'
            }}
          >
            Showing {filteredAllocations.length} allocation(s)
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '14px'
          }}
        >
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>
            Allocated Deals
          </div>
          <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>
            Click a card to open the full deal workspace
          </div>
        </div>

        {allocations.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No deals allocated yet</div>
            <div className="hv-empty-text">Allocate a deal to track revenue and rights for this project.</div>
          </div>
        ) : filteredAllocations.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No allocations match your filters</div>
            <div className="hv-empty-text">Try a different client or sort option.</div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '14px'
            }}
          >
            {filteredAllocations.map(allocation => {
              const deal = allocation.deals
              const sym = currencySymbol(allocation.currency || deal?.currency)
              const value = Number(allocation.amount || 0)
              const fullValue = Number(deal?.value || 0)
              const allocationPct = fullValue > 0 ? Math.round((value / fullValue) * 100) : null

              return (
                <div
                  key={allocation.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/deals/${deal?.id}`)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(`/deals/${deal?.id}`)
                    }
                  }}
                  style={{
                    background: '#fff',
                    border: '1px solid var(--hv-border)',
                    borderRadius: '14px',
                    padding: '18px',
                    minHeight: '190px',
                    boxShadow: '0 1px 3px rgba(15,21,35,0.05)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: 'var(--hv-text-primary)', marginBottom: '4px' }}>
                          {deal?.name || 'Untitled deal'}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)' }}>
                          {deal?.clients?.name || 'No client'}
                        </div>
                      </div>

                      <button
                        className="hv-btn hv-btn-ghost"
                        style={{
                          color: '#EF4444',
                          fontSize: '12px',
                          padding: '4px 6px',
                          alignSelf: 'flex-start'
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          setConfirmDelete(allocation.id)
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ marginTop: '22px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--hv-text-primary)', letterSpacing: '-0.5px' }}>
                        {sym}{value.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '4px' }}>
                        Project allocation
                        {allocationPct !== null ? ` · ${allocationPct}% of full deal` : ''}
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--hv-border-light)', paddingTop: '14px', marginTop: '22px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--hv-text-muted)', marginBottom: '4px' }}>
                          Notes / Rights Summary
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--hv-text-secondary)' }}>
                          {allocation.notes || 'No summary yet'}
                        </div>
                      </div>

                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#4F46E5', whiteSpace: 'nowrap' }}>
                        Open deal →
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Remove allocation"
          message={`Remove "${allocationToDelete?.deals?.name}" from this project?`}
          confirmLabel="Remove Allocation"
          onConfirm={() => deleteAllocation(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
